import {
  type CreateDrawingSetRequest,
  deleteDrawingSetRequest,
  getDrawingSets,
  postCarryForward,
  putAcceptDrawingSet,
  putActiveDrawingSet,
  saveDrawingSet,
  updateDrawingSet,
} from 'api/DrawingSetsApi'
import { deleteFolderKeepingSheets, deleteFolderRequest, getFolders, saveFolder, updateFolder } from 'api/FolderApi'
import {
  deleteArchivedProject,
  deleteDrawingPage,
  deleteProject,
  getArchivedProjects,
  getDrawingPage,
  getAvailableListProducts,
  getProjectById,
  getProjects,
  getSubscription,
  postDrawingPage,
  postDuplicateDrawingPage,
  putDrawingPage,
  putProject,
  restoreArchivedProject as restoreArchivedProjectAPI,
  toggleProjectMeasureSystem,
} from 'api/ProjectsAPI'
import { getProposals } from 'api/ProposalApi'
import { type SheetMetadataUpdate, putSheetMetadata } from 'api/SheetMetadataApi'
import type { Dispatch } from 'redux'
import { selectCurrentEditPage, updateProject } from 'reduxStore/edit'
import {
  addDrawingPageSuccess,
  addDrawingSetSuccess,
  addFolderSuccess,
  deleteArchivedProjectSuccess,
  deleteDrawingPageSuccess,
  deleteDrawingSetSuccess,
  deleteFolderError,
  deleteFolderSuccess,
  deleteProjectError,
  deleteProjectSuccess,
  editDrawingPageSucess,
  editProjectError,
  editProjectSuccess,
  getArchivedProjectsError,
  getArchivedProjectsRequest,
  getArchivedProjectsSuccess,
  getDrawingSetsSuccess,
  getFolderSuccess,
  getProjectsError,
  getProjectsRequest,
  getProjectsSuccess,
  getProposalsSuccess,
  getSubscriptionSuccess,
  pageMetadataSuccess,
  pagesAddedSuccess,
  restoreArchivedProjectError,
  restoreArchivedProjectSuccess,
  setActiveDrawingSetSuccess,
  setErrorMessage,
  setPageDetails,
  setProFeatureError,
  setSuccessMessage,
  updateDrawingSetSuccess,
  updateFolderSuccess,
} from 'reduxStore/projects/actions'
import type { DrawingSet, Project, ProjectFolder, drawingPage } from 'reduxStore/projects/types'
import { ShapeType, isPolygonShapeType } from 'utils/constants'
import { pushToDataLayer, trackOfflineEditSynced } from 'utils/eventPushes'
import { mergeOfflinePage } from 'utils/offlineMerge'
import { rememberServerPage, revisionOf, serverPageAt } from 'utils/serverPages'
import { type QueuedSave, addToOutbox, getQueuedSave, isNetworkError, queuedSaves, removeFromOutbox } from 'utils/saveOutbox'
import { type OrganizerResult, toFailure } from './pageListUpdates'
import { createPageSaveQueue } from './pageSaveQueue'

// Generic handler for API errors, specifically 402 pro feature errors
const handleApiError = (error: any, dispatch: Dispatch) => {
  if (error.response?.status === 402) {
    // Extract feature name from response data
    const featureName = error.response?.data?.featureName || 'UNKNOWN_FEATURE'
    dispatch(setProFeatureError(featureName))

    // Track pro feature blocked event
    pushToDataLayer({
      event: 'pro_feature_blocked',
      feature_name: featureName,
    })
    // Don't re-throw 402 errors since we're handling them with the modal
    return
  } else {
    // Handle other errors normally
    dispatch(setErrorMessage(error.message))
  }
  // Re-throw so components can still handle if needed
  throw error
}

export const getUserProjects = (page: number, search?: string) => (dispatch: Dispatch) => {
  dispatch(getProjectsRequest())

  return getProjects(page, search)
    .then((res) => {
      dispatch(getProjectsSuccess(res.data.content))
      dispatch(setPageDetails(res.data))
      return res
    })
    .catch(({ message }) => {
      dispatch(getProjectsError(message))
    })
}

export const getSubscriptionDetails = () => (dispatch: Dispatch) => {
  return getSubscription()
    .then((res) => {
      dispatch(getSubscriptionSuccess(res.data))
      return res
    })
    .catch(({ message }) => {
      dispatch(setErrorMessage(message))
    })
}

export const removeArchivedProject = (id: string) => (dispatch: Dispatch) => {
  return deleteArchivedProject(id)
    .then((res) => {
      dispatch(deleteArchivedProjectSuccess(id))
      pushToDataLayer({ event: 'archived_project_deleted', project_id: id })
      return res
    })
    .catch((err) => {
      const errorMessage = err.response?.data?.message || err.message || 'An error occurred'

      dispatch(deleteProjectError(errorMessage))
    })
}

export const restoreArchivedProject = (id: string) => (dispatch: Dispatch) => {
  return restoreArchivedProjectAPI(id)
    .then((res) => {
      dispatch(restoreArchivedProjectSuccess(id))
      dispatch(setSuccessMessage('Project restored successfully'))
      // Refresh the archived projects list to remove the restored project
      dispatch(getUserArchivedProjects('', 0))
      return res
    })
    .catch((err) => {
      const errorMessage = err.response?.data?.message || err.message || 'An error occurred'
      dispatch(restoreArchivedProjectError(errorMessage))
    })
}

// Edits still waiting on the device win over a loaded copy, which (offline, from cache) predates them.
const withQueuedSaves = async (project: Project, userId: string): Promise<Project> => {
  if (!userId) return project
  const queued = new Map((await queuedSaves(userId).catch(() => [])).map((entry) => [entry.pageId, entry.local]))
  if (queued.size === 0) return project
  return { ...project, drawingPageList: project.drawingPageList.map((page) => queued.get(page.id as string) ?? page) }
}

export const getProject = (id: string) => (dispatch: Dispatch, getState?: () => SaveState) => {
  return getProjectById(id)
    .then((res) => {
      for (const page of (res.data as Project).drawingPageList ?? []) rememberServerPage(page)
      return withQueuedSaves(res.data, String(getState?.().auth?.user?.uid ?? ''))
    })
    .then((project) => {
      dispatch(getProjectsSuccess([project]))
      return [project]
    })
    .catch(({ message }) => {
      dispatch(getProjectsError(message))
    })
}

export const getUserArchivedProjects = (search: string, page: number) => (dispatch: Dispatch) => {
  dispatch(getArchivedProjectsRequest())

  return getArchivedProjects(search, page)
    .then((res) => {
      dispatch(getArchivedProjectsSuccess(res.data.content))
      dispatch(setPageDetails(res.data))

      return res
    })
    .catch(({ message }) => {
      dispatch(getArchivedProjectsError(message))
    })
}

export const getProposalsByProjectId = (projectId: string) => (dispatch: Dispatch) => {
  return getProposals(projectId)
    .then((res) => {
      dispatch(getProposalsSuccess(res.data))
      return res
    })
    .catch(({ message }) => {
      dispatch(setErrorMessage(message))
    })
}

export const editUserProject = (data: Project) => (dispatch: Dispatch) => {
  return putProject(data)
    .then((res) => {
      dispatch(editProjectSuccess(res.data))
      dispatch(setSuccessMessage('Project Updated Successfully'))
      return res
    })
    .catch(({ message }) => {
      dispatch(editProjectError(message))
    })
}

export const switchProjectMeasure = (projectId: string) => (dispatch: Dispatch) => {
  return toggleProjectMeasureSystem(projectId)
    .then((res) => {
      dispatch(editProjectSuccess(res.data))
      getProject(projectId)(dispatch)
      return res
    })
    .catch(({ message }) => {
      dispatch(editProjectError(message))
    })
}
export const addDrawingPage = (data: drawingPage) => (dispatch: Dispatch) => {
  return postDrawingPage(data)
    .then((res) => {
      const currentPage = res.data
      dispatch(addDrawingPageSuccess(res.data))
      dispatch(setSuccessMessage('Drawing Page Added Successfully'))
      if (!currentPage) {
        dispatch(
          updateProject(
            currentPage.takeoffShapes.filter((item: any) => item.shapeType === ShapeType.LINEAR_LINE),
            currentPage.takeoffShapes.filter((item: any) => isPolygonShapeType(item.shapeType)),
            currentPage.takeoffShapes.filter((item: any) => item.shapeType === ShapeType.COUNT),
            currentPage.comments,
            currentPage.drawingScale,
          ),
        )
      }
      return res
    })
    .catch(({ message }) => {
      dispatch(editProjectError(message))
    })
}

export const duplicateDrawingPage = (data: drawingPage) => (dispatch: Dispatch) => {
  return postDuplicateDrawingPage(data)
    .then((res) => {
      dispatch(addDrawingPageSuccess(res.data))
      dispatch(setSuccessMessage('Drawing Page Duplicated Successfully'))
      return res
    })
    .catch(({ message }) => {
      dispatch(editProjectError(message))
    })
}

export const removeDrawingPage = (id: string, projectId: string) => (dispatch: Dispatch) => {
  return deleteDrawingPage(id, projectId)
    .then((res) => {
      dispatch(deleteDrawingPageSuccess(id, projectId))
      dispatch(setSuccessMessage('Drawing Page Deleted Successfully'))
      return res
    })
    .catch((err) => {
      const errorMessage = err.response?.data?.message || err.message || 'An error occurred'

      dispatch(deleteProjectError(errorMessage))
    })
}

// Full-page snapshots must reach the server in edit order. Only the newest
// response may refresh Redux, otherwise it can erase shapes drawn while saving.
const queuePageSave = createPageSaveQueue()

export const editDrawingPage = (data: drawingPage, update = true) => editDrawingPageWithoutDebounce(data, update)

type SaveState = Parameters<typeof selectCurrentEditPage>[0] & {
  projects?: { projectsList: Project[] }
  auth?: { user?: { uid?: string | number } | null }
}

const savedCopy = (state: SaveState | undefined, page: drawingPage) =>
  state?.projects?.projectsList
    .find((project) => project.id === page.projectId)
    ?.drawingPageList.find((candidate) => candidate.id === page.id)

// Offline, or with older offline edits still waiting: keep the save on this device so edits stay in order.
const keepOnDevice = async (dispatch: Dispatch, page: drawingPage, state: SaveState | undefined, base?: drawingPage) => {
  await addToOutbox(page, base ?? savedCopy(state, page) ?? page, String(state?.auth?.user?.uid ?? ''))
  dispatch(editDrawingPageSucess(page))
}

// The revision this tab's last unmerged save produced: the next save is built on it, so expecting it avoids refusing
// our own quick successive saves. Cleared after a merge, whose result holds edits the canvas may not have yet.
const ownRevision = new Map<string, number>()

class UnresolvedConflict extends Error {
  constructor(readonly base: drawingPage) {
    super('The page kept changing while saving')
  }
}

const conflictPage = (error: unknown) => {
  const response = (error as { response?: { status?: number; data?: drawingPage } }).response
  return response?.status === 409 ? response.data : undefined
}

/** Saves the page, refusing to overwrite newer saves: on a 409 it merges onto the newer page and tries again. */
async function saveWithoutOverwriting(data: drawingPage) {
  const pageId = data.id as string
  let expected = Math.max(revisionOf(data), ownRevision.get(pageId) ?? 0)
  let local = data
  let dataBase: drawingPage | undefined
  for (let attempt = 0; ; attempt++) {
    try {
      const res = await putDrawingPage(local, expected)
      rememberServerPage(res.data)
      if (attempt === 0) ownRevision.set(pageId, revisionOf(res.data))
      else ownRevision.delete(pageId)
      return res
    } catch (error) {
      const server = conflictPage(error)
      if (!server) throw error
      rememberServerPage(server)
      const base = serverPageAt(pageId, expected)
      // Without the exact version the edit was built on, a merge could undo someone's work: save as before.
      if (!base) return putDrawingPage(local)
      dataBase ??= base
      // Queued against the version this edit was built on, so a later merge still sees only this tab's changes.
      if (attempt === 2) throw new UnresolvedConflict(dataBase)
      local = mergeOfflinePage(base, local, server)
      expected = revisionOf(server)
    }
  }
}

export const editDrawingPageWithoutDebounce =
  (data: drawingPage, update: boolean) =>
  (dispatch: Dispatch, getState?: () => SaveState) => {
    if (!data.id) {
      dispatch(setErrorMessage('Unable to save drawing page without an ID'))
      return Promise.resolve(undefined)
    }
    return queuePageSave(data.id, async (isLatest) => {
      if (!navigator.onLine || (await getQueuedSave(data.id as string))) {
        await keepOnDevice(dispatch, data, getState?.())
        return undefined
      }
      try {
        const res = await saveWithoutOverwriting(data)
        if (!isLatest()) return res

        const currentPage = res.data
        dispatch(editDrawingPageSucess(currentPage))
        const isCurrentPage = !getState || selectCurrentEditPage(getState()).pageId === data.id
        if (currentPage && update && isCurrentPage) {
          dispatch(
            updateProject(
              currentPage.takeoffShapes.filter((item: any) => item.shapeType === ShapeType.LINEAR_LINE),
              currentPage.takeoffShapes.filter((item: any) => item.shapeType === ShapeType.POLYGON),
              currentPage.takeoffShapes.filter((item: any) => item.shapeType === ShapeType.COUNT),
              currentPage.comments,
              currentPage.drawingScale,
            ),
          )
        }
        return res
      } catch (error) {
        if (isNetworkError(error)) {
          await keepOnDevice(dispatch, data, getState?.())
          return undefined
        }
        if (error instanceof UnresolvedConflict) {
          // Keep it on the device and merge again shortly, once the page settles.
          await keepOnDevice(dispatch, data, getState?.(), error.base)
          setTimeout(() => void (dispatch as (thunk: unknown) => unknown)(replayOfflineSaves()), 2000)
          return undefined
        }
        dispatch(setErrorMessage(error instanceof Error ? error.message : 'Unable to save drawing page'))
      }
    })
  }

let replaying = false

/** Sends this user's offline saves, each merged onto the server's current page. Stops at the first network failure. */
export const replayOfflineSaves = () => async (dispatch: Dispatch, getState: () => SaveState) => {
  const userId = String(getState().auth?.user?.uid ?? '')
  if (replaying || !navigator.onLine || !userId) return
  replaying = true
  try {
    for (const entry of await queuedSaves(userId)) {
      const sent = await queuePageSave(entry.pageId, () => replaySave(entry, dispatch, getState))
      if (!sent) break
    }
  } finally {
    replaying = false
  }
}

const replaySave = async (entry: QueuedSave, dispatch: Dispatch, getState: () => SaveState): Promise<boolean> => {
  try {
    let server = (await getDrawingPage(entry.projectId, entry.pageId)).data as drawingPage
    let res: { data: unknown } | undefined
    // Someone may save between the read and this write; the server then returns its newer page to merge onto.
    for (let attempt = 0; !res; attempt++) {
      try {
        res = await putDrawingPage(mergeOfflinePage(entry.base, entry.local, server), server.revision ?? 0)
      } catch (error) {
        const conflict = (error as { response?: { status?: number; data?: drawingPage } }).response
        if (conflict?.status !== 409 || !conflict.data || attempt >= 2) throw error
        server = conflict.data
      }
    }
    rememberServerPage(res.data as drawingPage)
    ownRevision.delete(entry.pageId)
    await removeFromOutbox(entry)
    trackOfflineEditSynced({ projectId: entry.projectId, pageId: entry.pageId })
    // A newer offline edit was queued meanwhile; it replays next, and the canvas already shows it.
    if (await getQueuedSave(entry.pageId)) return true
    const saved = res.data as drawingPage
    dispatch(editDrawingPageSucess(saved))
    if (selectCurrentEditPage(getState()).pageId === entry.pageId) {
      const shapes = saved.takeoffShapes ?? []
      dispatch(
        updateProject(
          shapes.filter((item: any) => item.shapeType === ShapeType.LINEAR_LINE),
          shapes.filter((item: any) => isPolygonShapeType(item.shapeType)),
          shapes.filter((item: any) => item.shapeType === ShapeType.COUNT),
          saved.comments,
          saved.drawingScale,
        ),
      )
    }
    return true
  } catch (error) {
    if (isNetworkError(error)) return false
    // The server refused it (page deleted, access removed): drop it so it can't block the rest.
    await removeFromOutbox(entry)
    dispatch(setErrorMessage(`An edit made offline could not be saved: ${error instanceof Error ? error.message : ''}`))
    return true
  }
}

export const deleteUserProject = (id: string) => (dispatch: Dispatch) => {
  return deleteProject(id)
    .then((res) => {
      dispatch(deleteProjectSuccess(id))
      pushToDataLayer({ event: 'project_archived', project_id: id })
      return res
    })
    .catch(({ message }) => {
      dispatch(deleteProjectError(message))
    })
}

export const handleGetAllProducts = (token: string, query: string) => (dispatch: Dispatch) => {
  return getAvailableListProducts(token, { query: query })
}

export const addFolder = (projectFolder: ProjectFolder) => (dispatch: Dispatch) => {
  return saveFolder(projectFolder)
    .then((res) => {
      dispatch(addFolderSuccess(res.data))
      return res
    })
    .catch((error) => handleApiError(error, dispatch))
}

export const requsetFolders = (projectId: string) => (dispatch: Dispatch) => {
  return getFolders(projectId)
    .then((res) => {
      dispatch(getFolderSuccess(res.data))
      return res
    })
    .catch(({ message }) => {
      dispatch(setErrorMessage(message))
    })
}

export const deleteFolder = (folderId: string) => (dispatch: Dispatch) => {
  return deleteFolderRequest(folderId)
    .then((res) => {
      dispatch(deleteFolderSuccess(folderId))
      dispatch(setSuccessMessage('Folder Deleted Successfully'))
      return res
    })
    .catch((err) => {
      const errorMessage = err.response?.data?.message || err.message || 'An error occurred'
      dispatch(deleteFolderError(errorMessage))
    })
}

export const editFolder = (projectFolder: ProjectFolder) => (dispatch: Dispatch) => {
  return updateFolder(projectFolder)
    .then((res) => {
      dispatch(updateFolderSuccess(res.data))
      return res
    })
    .catch(({ message }) => {
      dispatch(setErrorMessage(message))
    })
}

export const requestDrawingSets = (projectId: string) => (dispatch: Dispatch) => {
  return getDrawingSets(projectId)
    .then((res) => {
      dispatch(getDrawingSetsSuccess(res.data))
      return res
    })
    .catch(({ message }) => {
      dispatch(setErrorMessage(message))
    })
}

export const addDrawingSet = (drawingSet: CreateDrawingSetRequest) => (dispatch: Dispatch) => {
  return saveDrawingSet(drawingSet)
    .then((res) => {
      // The backend materializes the ORIGINAL set on first use and stamps
      // existing pages, so refetch to pick up both the sets and the pages.
      if (res.data.originalSet) {
        dispatch(addDrawingSetSuccess(res.data.originalSet))
        getProject(drawingSet.projectId)(dispatch)
      }
      dispatch(addDrawingSetSuccess(res.data.createdSet))
      return res
    })
    .catch((error) => handleApiError(error, dispatch))
}

export const editDrawingSet = (drawingSet: DrawingSet) => (dispatch: Dispatch) => {
  return updateDrawingSet(drawingSet)
    .then((res) => {
      dispatch(updateDrawingSetSuccess(res.data))
      return res
    })
    .catch(({ message }) => {
      dispatch(setErrorMessage(message))
    })
}

export const deleteDrawingSet = (setId: string, projectId: string) => (dispatch: Dispatch) => {
  return deleteDrawingSetRequest(setId)
    .then((res) => {
      dispatch(deleteDrawingSetSuccess(setId))
      dispatch(setSuccessMessage('Drawing Set Deleted Successfully'))
      // Set deletion cascades to its pages — refetch the project.
      getProject(projectId)(dispatch)
      return res
    })
    .catch((err) => {
      const errorMessage =
        err.response?.status === 409
          ? 'Switch to another drawing set before deleting this one'
          : err.response?.data?.message || err.message || 'An error occurred'
      dispatch(setErrorMessage(errorMessage))
    })
}

export const setActiveDrawingSet = (projectId: string, drawingSetId: string | null) => (dispatch: Dispatch) => {
  return putActiveDrawingSet(projectId, drawingSetId)
    .then((res) => {
      dispatch(setActiveDrawingSetSuccess(projectId, drawingSetId))
      return res
    })
    .catch(({ message }) => {
      dispatch(setErrorMessage(message))
    })
}

// The Drawings organizer's saves return success or failure rather than toasting, so it never shows a
// false "saved" state and can say what went wrong where the edit was made.

/** "Use for takeoff": makes the set the working set, then refreshes every set's lifecycle. */
export const acceptDrawingSet =
  (projectId: string, setId: string) =>
  async (dispatch: Dispatch): Promise<OrganizerResult<Project>> => {
    try {
      const res = await putAcceptDrawingSet(setId)
      dispatch(setActiveDrawingSetSuccess(projectId, setId))
      await requestDrawingSets(projectId)(dispatch)
      return { ok: true, data: res.data }
    } catch (error) {
      return toFailure(error, "Couldn't use this set for takeoff. Try again.")
    }
  }

/** Copies a new issue's unchanged sheets in from the set it was based on. */
export const carryForwardSheets =
  (projectId: string, setId: string, sourceSetId: string, pageIds: string[]) =>
  async (dispatch: Dispatch): Promise<OrganizerResult<drawingPage[]>> => {
    try {
      const res = await postCarryForward(setId, sourceSetId, pageIds)
      dispatch(pagesAddedSuccess(projectId, res.data))
      return { ok: true, data: res.data }
    } catch (error) {
      return toFailure(error, "Couldn't carry the unchanged sheets into this set.")
    }
  }

/** Creates a folder for the Drawings organizer, reporting a failure instead of only toasting it. */
export const createOrganizerFolder =
  (folder: ProjectFolder) =>
  async (dispatch: Dispatch): Promise<OrganizerResult<ProjectFolder>> => {
    try {
      const res = await saveFolder(folder)
      dispatch(addFolderSuccess(res.data))
      return { ok: true, data: res.data }
    } catch (error) {
      return toFailure(error, "Couldn't create the folder. Try again.")
    }
  }

/** Renames or moves a folder; send the whole folder, the backend saves what it gets. */
export const updateOrganizerFolder =
  (folder: ProjectFolder) =>
  async (dispatch: Dispatch): Promise<OrganizerResult<ProjectFolder>> => {
    try {
      const res = await updateFolder(folder)
      dispatch(updateFolderSuccess(res.data))
      return { ok: true, data: res.data }
    } catch (error) {
      return toFailure(error, "Couldn't save the folder. Try again.")
    }
  }

/** Deletes a folder for one set: its sheets and subfolders move up a level, other sets' sheets stay. */
export const removeFolderKeepingSheets =
  (projectId: string, folderId: string, drawingSetId: string | null) =>
  async (dispatch: Dispatch): Promise<OrganizerResult<null>> => {
    try {
      await deleteFolderKeepingSheets(folderId, drawingSetId)
      await Promise.all([requsetFolders(projectId)(dispatch), getProject(projectId)(dispatch)])
      return { ok: true, data: null }
    } catch (error) {
      return toFailure(error, "Couldn't delete the folder. Try again.")
    }
  }

/** Catches the sheet list up with changes made elsewhere: only titles, numbers, stars, folders and the folder list. */
export const refreshSheetMetadata = (projectId: string) => async (dispatch: Dispatch) => {
  try {
    const [project, folders] = await Promise.all([getProjectById(projectId), getFolders(projectId)])
    dispatch(pageMetadataSuccess(projectId, project.data?.drawingPageList ?? []))
    dispatch(getFolderSuccess(folders.data))
  } catch {
    // A background catch-up; the next return to the tab tries again.
  }
}

/** Saves sheet titles, numbers, stars or folders; only those fields change. */
export const patchPagesMetadata =
  (projectId: string, updates: SheetMetadataUpdate[]) =>
  async (dispatch: Dispatch): Promise<OrganizerResult<drawingPage[]>> => {
    try {
      const res = await putSheetMetadata(projectId, updates)
      dispatch(pageMetadataSuccess(projectId, res.data))
      return { ok: true, data: res.data }
    } catch (error) {
      return toFailure(error, "Couldn't save that change. Try again.")
    }
  }
